import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * End-of-Day Administration Check banner.
 *
 * Renders a BIG RED mandatory acknowledgement card from 15:00 SAST onwards
 * (Mon-Sun — driven by the backend `show_banner` flag) until the user
 * ticks the box and confirms. Once acknowledged it collapses to a small
 * green confirmation strip for the rest of the day.
 *
 * The backend records who acknowledged and when.
 */
export default function EodCheckBanner() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const [status, setStatus] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/eod-check/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      // silent — if unauthenticated the banner just doesn't render
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    // Re-check every 5 min in case the day clock rolls past 15:00 SAST
    const iv = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  const submit = async () => {
    if (!confirmed) {
      toast.error('Please tick the acknowledgement box to confirm');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/eod-check/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('End-of-Day check acknowledged. You may sign out.');
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save acknowledgement');
    } finally {
      setSaving(false);
    }
  };

  if (!status || !status.required || !status.show_banner) return null;

  // Small confirmation strip if already acknowledged
  if (status.acknowledged) {
    return (
      <div className="mb-4 px-4 py-2 rounded-lg border border-emerald-500/50 bg-emerald-950/30 text-emerald-300 flex items-center gap-2 text-sm"
           data-testid="eod-ack-confirmed">
        <CheckCircle2 className="h-4 w-4" />
        <span>
          <strong>End-of-Day check acknowledged today</strong>
          {status.acknowledged_at && (
            <> at {new Date(status.acknowledged_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}</>
          )}
          {status.acknowledged_by && <> by {status.acknowledged_by}</>}
        </span>
      </div>
    );
  }

  // Big red mandatory banner
  return (
    <div
      className="mb-6 rounded-2xl border-4 border-red-500 bg-gradient-to-br from-red-700 via-red-600 to-red-800 text-white shadow-2xl shadow-red-900/40 overflow-hidden"
      data-testid="eod-ack-banner"
    >
      <div className="px-6 py-3 bg-red-900/60 flex items-center gap-3 border-b-2 border-red-400/40">
        <AlertTriangle className="h-8 w-8 text-yellow-300 animate-pulse" />
        <div>
          <div className="text-2xl md:text-3xl font-black uppercase tracking-wide leading-none">
            JSM — End-of-Day Administration Check
          </div>
          <div className="text-red-100 text-sm mt-1">16:40 · Mandatory before sign-out · Logout is blocked until acknowledged</div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <p className="text-lg md:text-xl font-semibold leading-snug">
          Before leaving for the day, you are required to ensure that:
        </p>
        <ul className="text-base md:text-lg space-y-1 pl-6 list-disc marker:text-yellow-200">
          <li>All emails have been checked and actioned.</li>
          <li>All tasks for the day have been completed or appropriately handed over.</li>
          <li>Any outstanding matters have been communicated to your supervisor.</li>
        </ul>

        <div className="bg-red-950/60 border-2 border-red-300/40 rounded-lg p-4 mt-4">
          <div className="text-yellow-200 uppercase text-xs tracking-widest font-bold mb-2">Mandatory Acknowledgement</div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-5 w-5 accent-yellow-300 flex-shrink-0"
              data-testid="eod-ack-checkbox"
            />
            <span className="text-base md:text-lg leading-snug">
              I, the Administrator, hereby confirm that all my tasks have been completed and that all emails have been checked and are up to date.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
          <div className="text-xs text-red-100">
            This acknowledgement must be completed before the end of each working day <span className="font-bold uppercase">BEFORE SIGN OUT</span>.
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!confirmed || saving}
            className="bg-yellow-300 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed text-red-900 font-black uppercase tracking-wide px-6 py-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
            data-testid="eod-ack-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            {saving ? 'Saving…' : 'Acknowledge & Sign Out Ready'}
          </button>
        </div>
      </div>
    </div>
  );
}
